import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Icon } from '../../components/Icon.tsx';
import { Dialog } from '../../components/Dialog.tsx';
import { OptionList } from '../../components/SelectField.tsx';
import { Pagination } from '../../components/Pagination.tsx';
import { useAuthStore } from '../../store/auth.ts';
import { useIssues, useZones } from '../../features/zone/api.ts';
import { StatusChip } from '../../features/zone/StatusChip.tsx';
import { PriorityMark } from '../../features/zone/PriorityMark.tsx';
import {
  PRIORITY_LABEL, STATUS_LABEL,
  type IssueFilters, type IssuePriority, type IssueStatus,
} from '../../features/zone/types.ts';

const PAGE_SIZE = 20;

type MenuKey = 'status' | 'zone' | 'priority';

function pick<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const chipBase =
  'inline-flex h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-3.5 text-sm font-medium transition-colors';
const chipOff = 'border-[hsl(var(--border-strong))] text-muted-foreground hover:bg-[hsl(var(--surface-high))]';
const chipOn = 'border-primary bg-primary/10 text-primary';

function Chip({
  label, active, onClick, menu,
}: { label: string; active: boolean; onClick: () => void; menu?: boolean }): JSX.Element {
  return (
    <button type="button" onClick={onClick} aria-pressed={menu ? undefined : active} className={`${chipBase} ${active ? chipOn : chipOff}`}>
      {label}
      {menu && <Icon name="arrow_drop_down" size={20} className="-me-1.5" />}
    </button>
  );
}

export function ZoneIssues(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const sp = useSearch({ strict: false }) as Record<string, unknown>;

  const status = pick<IssueStatus>(sp.status, ['SUBMITTED', 'ASSIGNED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']);
  const priority = pick<IssuePriority>(sp.priority, ['LOW', 'NORMAL', 'URGENT']);
  const zoneId = typeof sp.zone === 'string' && sp.zone ? sp.zone : undefined;
  const mine = sp.mine === '1' || sp.mine === 1 || sp.mine === true;
  const q = typeof sp.q === 'string' ? sp.q : typeof sp.q === 'number' ? String(sp.q) : '';
  const pageNum = Number(sp.page);
  const page = Number.isInteger(pageNum) && pageNum > 0 ? pageNum : 1;

  const setParams = (patch: Record<string, string | undefined>): void => {
    void navigate({
      to: '/zone',
      replace: true,
      search: ((prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
        return next;
      }) as never,
    });
  };

  // Search box is local state; the URL follows 300ms after typing stops.
  const [search, setSearch] = useState(q);
  useEffect(() => {
    if (search.trim() === q) return;
    const t = setTimeout(() => setParams({ q: search.trim() || undefined, page: undefined }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filters: IssueFilters = { status, priority, zoneId, mine: mine || undefined, q: q || undefined, page, pageSize: PAGE_SIZE };
  const { data, isLoading, isError } = useIssues(filters);
  const { data: zones } = useZones();

  const zoneRole = user?.zoneRole ?? null;
  const mineLabel = zoneRole === 'COUNCILLOR' ? 'My issues' : zoneRole === 'STAFF' ? 'Assigned to me' : zoneRole === 'SECRETARY_GENERAL' ? 'To triage' : null;

  const [menu, setMenu] = useState<MenuKey | null>(null);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const filtered = !!(status || priority || zoneId || mine || q);

  const zoneName = zones?.find((z) => z.id === zoneId)?.name;
  const menuOptions: Record<MenuKey, { title: string; current?: string; options: Array<{ value: string; label: string }>; param: string }> = {
    status: {
      title: 'Status', current: status, param: 'status',
      options: (Object.keys(STATUS_LABEL) as IssueStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s] })),
    },
    zone: {
      title: 'Zone', current: zoneId, param: 'zone',
      options: (zones ?? []).map((z) => ({ value: z.id, label: z.name })),
    },
    priority: {
      title: 'Priority', current: priority, param: 'priority',
      options: (Object.keys(PRIORITY_LABEL) as IssuePriority[]).map((p) => ({ value: p, label: PRIORITY_LABEL[p] })),
    },
  };
  const active = menu ? menuOptions[menu] : null;

  const canCreate = zoneRole === 'COUNCILLOR';

  return (
    <div className="page space-y-5 md:space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl leading-tight tracking-tight md:text-[2.5rem]">Issues</h1>
          {total > 0 && <span className="num tabular text-sm text-muted-foreground">{total}</span>}
        </div>
        {canCreate && (
          <Link to="/zone/new" className="btn-primary hidden md:inline-flex">
            <Icon name="add" size={18} /> New issue
          </Link>
        )}
      </header>

      <div className="rounded-full bg-[hsl(var(--chrome))] p-2 sm:px-3">
        <div className="relative w-full sm:max-w-md">
          <Icon
            name="search"
            size={18}
            className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            className="w-full rounded-full bg-[hsl(var(--background))] py-2 pe-4 ps-11 text-base outline-none transition focus:ring-2 focus:ring-ring/30"
            value={search}
            placeholder="Search issues"
            aria-label="Search issues"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 [scrollbar-width:none]">
        {mineLabel && (
          <Chip label={mineLabel} active={mine} onClick={() => setParams({ mine: mine ? undefined : '1', page: undefined })} />
        )}
        <Chip label={status ? STATUS_LABEL[status] : 'Status'} active={!!status} menu onClick={() => setMenu('status')} />
        <Chip label={zoneName ?? 'Zone'} active={!!zoneId} menu onClick={() => setMenu('zone')} />
        <Chip label={priority ? PRIORITY_LABEL[priority] : 'Priority'} active={!!priority} menu onClick={() => setMenu('priority')} />
      </div>

      {isLoading ? (
        <p className="status-label" role="status" aria-live="polite">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-destructive" role="alert">Could not load issues. Please try again.</p>
      ) : items.length === 0 ? (
        <div className="space-y-3 py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {filtered ? 'No issues match these filters.' : canCreate ? 'No issues yet. Report the first one.' : 'No issues yet.'}
          </p>
          {filtered ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setSearch(''); setParams({ status: undefined, zone: undefined, priority: undefined, mine: undefined, q: undefined, page: undefined }); }}
            >
              Clear filters
            </button>
          ) : canCreate ? (
            <Link to="/zone/new" className="btn-ghost">New issue</Link>
          ) : null}
        </div>
      ) : (
        <>
          <ul className="overflow-hidden rounded-3xl border border-[hsl(var(--border)/0.7)]">
            {items.map((i) => (
              <li key={i.id} className="border-b border-[hsl(var(--border)/0.6)] last:border-b-0">
                <Link
                  to="/zone/issues/$id"
                  params={{ id: i.id }}
                  className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
                >
                  <span className="mono-meta w-10 shrink-0 text-muted-foreground">#{i.number}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[0.95rem] font-medium text-ink">{i.title}</div>
                    <div className="truncate text-sm text-muted-foreground">
                      {[i.zone.name, i.category?.name, timeAgo(i.createdAt)].filter(Boolean).join(' · ')}
                      {i.assignee ? ` · ${i.assignee.name}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <StatusChip status={i.status} />
                    {i.priority === 'URGENT' && <PriorityMark priority={i.priority} />}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <Pagination
            page={data?.page ?? page}
            pageSize={data?.pageSize ?? PAGE_SIZE}
            total={total}
            onChange={(p) => setParams({ page: p > 1 ? String(p) : undefined })}
          />
        </>
      )}

      {canCreate && (
        <Link
          to="/zone/new"
          aria-label="New issue"
          className="fixed end-4 z-30 grid size-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95 md:hidden"
          style={{ bottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
        >
          <Icon name="add" size={26} />
        </Link>
      )}

      <Dialog open={!!menu} onClose={() => setMenu(null)} title={active?.title ?? ''}>
        {active && (
          <OptionList
            options={[{ value: '', label: 'All' }, ...active.options]}
            value={active.current ?? ''}
            onSelect={(v) => { setParams({ [active.param]: v || undefined, page: undefined }); setMenu(null); }}
          />
        )}
      </Dialog>
    </div>
  );
}
